import { FormControl, IconButton, makeStyles, MenuItem, OutlinedInput, Select } from '@material-ui/core';
import { IDishContent, IPriceModelContent, ISeatingContent, IVersionedObject } from '../../planEatApi/PlanEatFacade';
import { from_YYYYMMDDHHMMSS, prettyDate, to_YYYYMMDDHHMMSS } from '../../config/dateFormatting';
import { Modal } from '../ui/Modal';
import { clone } from '../../utils/clone';
import { CancelCrudButtons } from '../ui/CancelCrudButtons';
import { InputField } from '../ui/InputField';
import { useEffect, useState } from 'react';
import { CustomCheckBox } from '../ui/CustomCheckBox';
import { DishCreationModal } from './DishCreationModal';
import { MdClose, MdDelete, MdEdit } from 'react-icons/md';
import { NEW_ITEM_ID } from '../../constants/constants';
import { resetDish } from '../../utils/resetObjects';
import { getSeatingsOnDate } from '../../utils/getSeatingsOnDate';
import { getDistinctDishesOnSeatings } from '../../utils/getDistinctDishesOnSeatings';

const useStyles = makeStyles((theme: any) => ({
    dateTitle: {
        marginBottom: 24,
        fontWeight: "bold",
        fontSize: 18
    },
    inputContent: {
        display: "grid",
        rowGap: 25,
        gridTemplateColumns: "100%",

        "& > *": {
            display: "grid",
            gridTemplateColumns: "30% 70%",
        }
    },
    inputTitle: {
        marginBottom: 24,
        fontWeight: "bold",
        fontSize: 16
    },
    timeSelectionContainer: {
        display: "grid",
        gridTemplateColumns: "auto 40px auto",
        minWidth: "100%",

        "& > *": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
        }
    },
    checkBoxContainer: {
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        marginBottom: 20,
        columnGap: 10,
        "& > *": {
            "&:hover": {
                cursor: "pointer"
            }
        }
    },
    buttonDisabled: {
        pointerEvents: "none",
        opacity: "50%"
    }
}));

interface Props {
    hidden:boolean;
    setHidden:  React.Dispatch<React.SetStateAction<any>>;
    seating: ISeatingContent;
    setSeating:  React.Dispatch<React.SetStateAction<ISeatingContent>>;
    dish: IDishContent;
    setDish:  React.Dispatch<React.SetStateAction<IDishContent>>;
    date: Date;
    postSeating: (dishId?: string) => void;
    priceModels: IVersionedObject<IPriceModelContent>[];
    seatings: IVersionedObject<ISeatingContent>[];
    dishes:  IVersionedObject<IDishContent>[];
}


export const SeatingCreationModal: React.FC<Props> = ({
    hidden,
    setHidden,
    seating,
    setSeating,
    dish,
    setDish,
    date,
    postSeating,
    priceModels,
    seatings,
    dishes
}) => {

    const classes = useStyles()
    const [createNewDishModalHidden, setCreateNewDishModalHidden] = useState(true)
    const [dishId, setDishId] = useState("")
    const [dishesOnDate,setDishesOnDate] = useState<IVersionedObject<IDishContent>[]>([]);

    useEffect(() => {
        const seatingsOnDate = getSeatingsOnDate(date,seatings)
        const tmpDishesOnDate = getDistinctDishesOnSeatings(dishes,seatingsOnDate);
        setDishesOnDate(tmpDishesOnDate)
        if(seating.seatingDishes.length > 0){
            setDishId(seating.seatingDishes[0].dishId)
        } else {
            if(tmpDishesOnDate.length > 0){
                setDishId(tmpDishesOnDate[0].content.id)
            }
        }
    },[date,seatings,dishes])

    const handleChange = (prop: keyof ISeatingContent) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setSeating({ ...seating, [prop]: event.target.value });
    };

    const handleSeatingTimeChange = (time:string,type:string) =>  {
        const [hours, minutes] = time.split(':');
        var seatingTime = new Date(date)
        seatingTime.setHours(parseInt(hours))
        seatingTime.setMinutes(parseInt(minutes))

        var tmpSeating: ISeatingContent = clone<ISeatingContent>(seating)
        if(type=== "start"){
            tmpSeating.seatingStart = to_YYYYMMDDHHMMSS(seatingTime)
        } else if ("end") {
            tmpSeating.seatingEnd = to_YYYYMMDDHHMMSS(seatingTime)
        }
        setSeating(tmpSeating)
    }

    const convertToTimeString = (tmpDate: Date) => {
        var hours = "00"
        var minutes = "00"

        if (tmpDate.getHours() >= 0 && tmpDate.getHours() <= 9) {
            hours = '0' + tmpDate.getHours();
        } else {
            hours = '' + tmpDate.getHours();
        }
        
        var minutes = '';
        if (tmpDate.getMinutes() >= 0 && tmpDate.getMinutes() <= 9) {
            minutes = '0' + tmpDate.getMinutes();
        } else {
            minutes = '' + tmpDate.getMinutes();
        }
        
        return hours + ":" + minutes
    }

    function postDish(){
        setCreateNewDishModalHidden(true)
        dish.id = NEW_ITEM_ID
        setDish(dish)
    }

    function createSeating(){
        dishId !== "" ? postSeating(dishId) : postSeating()
    }

    const handlePriceModelChange = (event: any) => {
        priceModels.forEach(priceModel => {
            if(event.target.value=== priceModel.content.id){
                var tmpSeating: ISeatingContent = clone<ISeatingContent>(seating)
                tmpSeating.priceModelId = priceModel.content.id
                tmpSeating.priceModelVersion = priceModel.version
                setSeating(tmpSeating)
            }
        })
    };

    const handleDishIdChange = (event: any) => {
        const dish = dishesOnDate.find(dishOnDate => dishOnDate.content.id == event.target.value)
        if(dish){
            var tmpSeating: ISeatingContent = clone<ISeatingContent>(seating)
            tmpSeating.seatingDishes = [
                {
                    dishId: dish.content.id,
                    menuQuantity: seating.seatingCapacity
                }
            ]
            setSeating(tmpSeating)
            setDishId(event.target.value)
        }
    };

    function isValidInput(){
        if(seating.seatingName.length <= 0){
            return false
        } else if (seating.seatingCapacity == 0){
            return false
        } else if(dishId === "" && seating.seatingDishes.length <= 0){
            return false
        } else if (seating.priceModelId === ""){
            return false
        } else if (seating.seatingStart.length === 0 || seating.seatingEnd.length === 0){
            return false
        } else {
            return true
        }
    }

    return (
        <Modal title={seating.id !== '' ? "Update Seating  " : "New Seating  "} hidden={hidden} setHidden={setHidden}>
            <div className={classes.dateTitle}>{prettyDate(date,false,true)}</div>
            <div className={classes.inputContent}>
                <div>
                    <div className={classes.inputTitle}>Seating name</div>
                    <InputField
                        variant="outlined"
                        value={seating.seatingName}
                        onChange={handleChange('seatingName')}
                        fullWidth
                    />
                </div>
                <div>
                    <div className={classes.inputTitle}>Seating Capacity</div>
                    <InputField
                        variant="outlined"
                        type="number"
                        value={seating.seatingCapacity}
                        onChange={handleChange('seatingCapacity')}
                        fullWidth
                    />
                </div>
                <div>
                    <div className={classes.inputTitle}>Time</div>
                    <div className={classes.timeSelectionContainer}>
                        <InputField
                            id="time"
                            type="time"
                            variant="outlined"
                            value={convertToTimeString(from_YYYYMMDDHHMMSS(seating.seatingStart))}
                            onChange={(e) => {handleSeatingTimeChange(e.target.value,"start")}}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                        <div>to</div>
                        <InputField
                            id="time"
                            type="time"
                            variant="outlined"
                            value={convertToTimeString(from_YYYYMMDDHHMMSS(seating.seatingEnd))}
                            onChange={(e) => {handleSeatingTimeChange(e.target.value,"end")}}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </div>
                </div>
                <div>
                    <div className={classes.inputTitle}>Dish</div>
                    <div>
                        {
                            dish.id === "" ?
                            <div className={classes.checkBoxContainer} onClick={() => {setCreateNewDishModalHidden(false)}}>
                                <CustomCheckBox selected={!createNewDishModalHidden}/> Create new dish
                            </div>
                            :
                            <div className={classes.checkBoxContainer}>
                                <CustomCheckBox selected={true}/>
                                Create new dish - <b>{dish.dishTitle}</b>  
                                <IconButton color='primary' onClick={() => {setCreateNewDishModalHidden(false)}} ><MdEdit size={16} /></IconButton>
                                <IconButton color="secondary" onClick={() => {setDish(resetDish)}} ><MdClose size={16} /></IconButton>
                            </div>
                        }
                        {
                            dish.id === "" &&
                            <div>
                                <div>
                                    {
                                        createNewDishModalHidden && dishesOnDate.length > 0 ?
                                        <div>
                                            <div className={classes.checkBoxContainer} onClick={() => {setCreateNewDishModalHidden(true)}}>
                                                <CustomCheckBox selected={createNewDishModalHidden} /> Choose existing dish
                                            </div>
                                            <FormControl variant="outlined" style={{width: "100%"}}>
                                                <Select
                                                    variant="outlined"
                                                    value={dishId}
                                                    onChange={handleDishIdChange}
                                                    fullWidth
                                                >
                                                    {dishesOnDate.map(dishOnDate => <MenuItem key={dishOnDate.content.id} value={dishOnDate.content.id}>{dishOnDate.content.dishTitle}</MenuItem>)}
                                                </Select>
                                            </FormControl>
                                        </div>

                                        : createNewDishModalHidden &&
                                        <div>
                                            No existing dish on date, please create a new dish
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                      
                    </div>
                </div>
                <div>
                    <div className={classes.inputTitle}>Price Model</div>
                    <FormControl variant="outlined">
                        <Select
                            input={<OutlinedInput notched label="Price Model" />}
                            id="priceModel"
                            variant="outlined"
                            value={seating.priceModelId}
                            onChange={handlePriceModelChange}
                            fullWidth
                        >
                            {priceModels.map(priceModel => <MenuItem key={priceModel.content.id} value={priceModel.content.id}>{priceModel.content.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </div>
            </div>
            <div>
                <CancelCrudButtons 
                    hidden={hidden} 
                    setHidden={setHidden} 
                    postButtonText={seating.id !== '' ? "Update" : "Create" + " Seating"} 
                    isDelete={false} 
                    postEntity={createSeating} 
                    disabledPostButton={!isValidInput()}
                />
            </div>
            <DishCreationModal 
                hidden={createNewDishModalHidden}        
                setHidden={setCreateNewDishModalHidden}
                date={date}
                dish={dish}
                setDish={setDish}
                postDish={postDish}
            />
        </Modal>
    );
}